import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, UserCheck, Crown, TrendingUp, Activity } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { userManagementService } from '@/services/admin/user-management-service';
import { Skeleton } from '@/components/ui/skeleton';

export const UserStatsCards = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => userManagementService.getUserStats(),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers?.toLocaleString() || '0',
      icon: Users,
      description: 'Registered users',
      color: 'text-blue-600'
    },
    {
      title: 'Active Users',
      value: stats?.activeUsers?.toLocaleString() || '0',
      icon: Activity,
      description: 'Active in last 30 days',
      color: 'text-green-600'
    },
    {
      title: 'Premium Users',
      value: stats?.premiumUsers?.toLocaleString() || '0',
      icon: Crown,
      description: 'Paid subscriptions',
      color: 'text-yellow-600'
    },
    {
      title: 'Affiliate Referrals',
      value: stats?.affiliateReferrals?.toLocaleString() || '0',
      icon: UserCheck,
      description: 'From affiliates',
      color: 'text-purple-600'
    },
    {
      title: 'Growth Rate',
      value: `${stats?.growthRate || 0}%`,
      icon: TrendingUp,
      description: 'Last 30 days',
      color: stats?.growthRate && stats.growthRate > 0 ? 'text-green-600' : 'text-red-600'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {statCards.map((stat, index) => (
        <Card key={index} className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stat.description}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};